import queryString from 'query-string';
import type { IssueTypeResourceData } from '@atlassian/jira-admin-pages-issue-types-common/src/types.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import type { Action } from '@atlassian/react-sweet-state';
import { getIssueTypes } from '../../services/issue-types/main.tsx';
import type { Filter } from '../../services/issue-types/types';
import { toUrlTransformer } from '../../services/issue-types/utils/transformers';
import type { IssueTypesStore, ContainerProps } from './types';

export const actions = {
	storeResourceResult:
		(
			data: IssueTypeResourceData | null,
			error: Error | null,
			isLoading: boolean,
		): Action<IssueTypesStore> =>
		({ getState, setState }) => {
			if (getState().isResourceFetchComplete) return;

			setState({
				data,
				error,
				isLoading,
				isResourceFetchComplete: true,
			});
		},
	updateFilter:
		(
			currentQueryString: string,
			newFilter: Filter,
			updateQueryString: (path: string) => void,
		): Action<IssueTypesStore, ContainerProps> =>
		() => {
			const currentFilter = queryString.parse(currentQueryString);
			const filter = {
				...currentFilter,
				...newFilter,
			};
			updateQueryString(`?${queryString.stringify(toUrlTransformer(filter))}`);
		},
	fetchIssueTypes:
		(qs: string): Action<IssueTypesStore, ContainerProps> =>
		async ({ getState, setState }) => {
			try {
				// If fetch completed during SSR, and it's the first CSR render, don't redundantly fetch again
				if (
					getState().isResourceFetchComplete &&
					!getState().isInitialCsrRenderComplete &&
					!getState().isLoading
				) {
					return;
				}

				setState({
					isLoading: true,
				});

				const data = await getIssueTypes(qs);
				setState({
					data,
					isLoading: false,
					error: null,
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				setState({
					data: null,
					isLoading: false,
					error,
				});
				fireErrorAnalytics({
					error,
					meta: {
						id: 'fetchIssueTypes',
						packageName: 'jiraAdminPagesIssueTypesResources',
						teamName: 'morphinecake',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		},
	setInitialCsrRenderComplete:
		(): Action<IssueTypesStore, ContainerProps> =>
		({ setState }) => {
			setState({
				isInitialCsrRenderComplete: true,
			});
		},
} as const;
